// System Map: every HydroTools module drawn around the core platform, from the module.json file
// next to each tool (src/tools/*/module.json) plus docs/system-map/*.json. Admin-only.
// Nothing here is hand-maintained: add or edit a module.json and the map follows on the next build.
import areasJson from "../../../docs/system-map/areas.json";
import coreJson from "../../../docs/system-map/core.json";
import servicesJson from "../../../docs/system-map/services.json";
import workspacesJson from "../../../docs/system-map/workspaces.json";
import roadmapJson from "../../../docs/system-map/roadmap.json";

const MODULE_FILES = import.meta.glob("../*/module.json", { eager: true, import: "default" });
const REPO_TREE = "https://github.com/hydrotools-ui/app/tree/staging/";
const STATUSES = ["live", "beta", "dev", "planned", "retired"];
const STATUS_LABEL = { live: "Live", beta: "Beta", dev: "In development", planned: "Planned", retired: "Retired" };
const VIEW_KEY = "hydrotools:system-map:view:v1";

// Layout constants (SVG user units).
const TILE_W = 168, TILE_H = 40, TILE_GAP = 8, CL_PAD = 16, CL_HEAD = 46, CL_FOOT = 34, CELL_GAP = 56;

function esc(v) {
  return String(v ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}
const arr = (v) => (Array.isArray(v) ? v : []);

function loadModel() {
  const areas = arr(areasJson).slice().sort((a, b) => (a.order ?? 99) - (b.order ?? 99));
  const core = arr(coreJson);
  const services = arr(servicesJson);
  const roadmap = arr(roadmapJson);
  const modules = [];
  for (const m of Object.values(MODULE_FILES)) if (m && m.id) modules.push({ ...m, kind: "module" });
  for (const ws of arr(workspacesJson)) {
    modules.push({ ...ws, kind: "workspace", tools: [], modules: undefined });
    for (const sub of arr(ws.modules)) modules.push({ ...sub, kind: "workspace-module", workspace: ws.id, workspaceName: ws.name });
  }
  const byId = new Map(modules.map((m) => [m.id, m]));
  const coreById = new Map(core.map((c) => [c.id, c]));
  const roadmapByModule = new Map();
  for (const item of roadmap) for (const id of arr(item.modules)) {
    if (!roadmapByModule.has(id)) roadmapByModule.set(id, []);
    roadmapByModule.get(id).push(item);
  }
  const dependents = new Map();
  for (const m of modules) for (const d of arr(m.dependsOn)) {
    if (!dependents.has(d)) dependents.set(d, []);
    dependents.get(d).push(m.id);
  }
  return { areas, core, services, roadmap, modules, byId, coreById, roadmapByModule, dependents };
}

function openItems(items) {
  return arr(items).filter((i) => !/^(done|shipped|closed|complete)/i.test(String(i.status || "")));
}

// Ring of cells around the core: 3 columns x 4 rows, the middle column of rows 1-2 is the core.
const RING = [[0, 0], [1, 0], [2, 0], [2, 1], [2, 2], [2, 3], [1, 3], [0, 3], [0, 2], [0, 1]];
// Preferred cells, so neighbours on the map are neighbours in the workflow and the biggest area
// (processing) sits beside the core instead of stretching a top or bottom row.
const PREFERRED_CELL = {
  planning: [0, 0], acquisition: [1, 0], "surfaces-3d": [2, 0], processing: [2, 1], "data-delivery": [2, 2],
  "charts-env": [2, 3], marina: [1, 3], operations: [0, 3], ai: [0, 2],
};

function layout(model, visibleIds) {
  const { areas } = model;
  const ringAreas = areas.filter((a) => a.id !== "platform");
  const clusters = ringAreas.map((a) => ({ area: a, mods: model.modules.filter((m) => m.area === a.id && visibleIds.has(m.id)) }));
  const platformMods = model.modules.filter((m) => m.area === "platform" && visibleIds.has(m.id));
  const unknown = model.modules.filter((m) => !areas.some((a) => a.id === m.area) && visibleIds.has(m.id));
  if (unknown.length) clusters.push({ area: { id: "_other", name: "Other" }, mods: unknown });

  const sizeOf = (n, cols) => {
    const rows = Math.max(1, Math.ceil(n / cols));
    return { w: CL_PAD * 2 + cols * TILE_W + (cols - 1) * TILE_GAP, h: CL_HEAD + CL_FOOT + rows * TILE_H + (rows - 1) * TILE_GAP + CL_PAD };
  };
  for (const c of clusters) {
    const n = c.mods.length;
    c.cols = n > 24 ? 5 : n > 12 ? 4 : n > 6 ? 3 : 2;
    Object.assign(c, sizeOf(c.mods.length, c.cols));
  }
  // Core card: core chips + platform tools, 3 columns.
  const coreCols = 3;
  const coreRows = Math.ceil(model.core.length / coreCols);
  const platRows = Math.ceil(platformMods.length / coreCols);
  const coreW = CL_PAD * 2 + coreCols * TILE_W + (coreCols - 1) * TILE_GAP;
  const coreH = CL_HEAD + coreRows * (TILE_H + TILE_GAP) + (platRows ? 30 + platRows * (TILE_H + TILE_GAP) : 0) + CL_PAD;

  const colW = [0, coreW, 0];
  const rowH = [0, 0, 0, 0];
  const taken = new Set();
  for (const c of clusters) {
    const pref = PREFERRED_CELL[c.area.id];
    if (pref && !taken.has(String(pref))) { c.cell = pref; taken.add(String(pref)); }
  }
  for (const c of clusters) {
    if (c.cell) continue;
    c.cell = RING.find((cell) => !taken.has(String(cell))) || [0, 1];
    taken.add(String(c.cell));
  }
  clusters.forEach((c) => {
    const cell = c.cell;
    colW[cell[0]] = Math.max(colW[cell[0]], c.w);
    rowH[cell[1]] = Math.max(rowH[cell[1]], c.h);
  });
  colW[1] = Math.max(colW[1], coreW);
  const midH = Math.max(rowH[1] + rowH[2], coreH);
  if (rowH[1] + rowH[2] < midH) { const extra = (midH - rowH[1] - rowH[2]) / 2; rowH[1] += extra; rowH[2] += extra; }
  const colX = [0, colW[0] + CELL_GAP, colW[0] + colW[1] + CELL_GAP * 2];
  const rowY = [0, rowH[0] + CELL_GAP, rowH[0] + rowH[1] + CELL_GAP * 2, rowH[0] + rowH[1] + rowH[2] + CELL_GAP * 3];
  const width = colX[2] + colW[2];
  const height = rowY[3] + rowH[3];

  const tiles = new Map();
  for (const c of clusters) {
    const [cx, cy] = c.cell;
    // Align toward the core: left column hugs right, right column hugs left, top row hugs bottom.
    c.x = colX[cx] + (cx === 0 ? colW[0] - c.w : cx === 1 ? (colW[1] - c.w) / 2 : 0);
    c.y = rowY[cy] + (cy === 0 ? rowH[0] - c.h : cy === 3 ? 0 : (rowH[cy] - c.h) / 2);
    c.mods.forEach((m, i) => {
      const col = i % c.cols, row = Math.floor(i / c.cols);
      tiles.set(m.id, { x: c.x + CL_PAD + col * (TILE_W + TILE_GAP), y: c.y + CL_HEAD + row * (TILE_H + TILE_GAP), w: TILE_W, h: TILE_H, m });
    });
  }
  const coreBox = { x: colX[1] + (colW[1] - coreW) / 2, y: rowY[1] + (midH - coreH) / 2, w: coreW, h: coreH };
  const coreTiles = new Map();
  model.core.forEach((c, i) => {
    const col = i % coreCols, row = Math.floor(i / coreCols);
    coreTiles.set(c.id, { x: coreBox.x + CL_PAD + col * (TILE_W + TILE_GAP), y: coreBox.y + CL_HEAD + row * (TILE_H + TILE_GAP), w: TILE_W, h: TILE_H, c });
  });
  const platTop = coreBox.y + CL_HEAD + coreRows * (TILE_H + TILE_GAP) + 30;
  platformMods.forEach((m, i) => {
    const col = i % coreCols, row = Math.floor(i / coreCols);
    tiles.set(m.id, { x: coreBox.x + CL_PAD + col * (TILE_W + TILE_GAP), y: platTop + row * (TILE_H + TILE_GAP), w: TILE_W, h: TILE_H, m });
  });
  return { clusters, coreBox, coreTiles, tiles, platTop: platformMods.length ? platTop : null, width, height };
}

function productsOf(mods) {
  const seen = new Map();
  for (const m of mods) for (const p of arr(m.produces)) {
    const k = String(p).trim();
    if (k) seen.set(k.toLowerCase(), k);
  }
  return [...seen.values()];
}

function curve(a, b) {
  const ax = a.x + a.w / 2, ay = a.y + a.h / 2, bx = b.x + b.w / 2, by = b.y + b.h / 2;
  const mx = (ax + bx) / 2, my = (ay + by) / 2;
  const dx = bx - ax, dy = by - ay;
  const len = Math.hypot(dx, dy) || 1;
  const bend = Math.min(80, len * 0.15);
  return `M${ax},${ay} Q${mx - (dy / len) * bend},${my + (dx / len) * bend} ${bx},${by}`;
}

const STYLE = `
.smap-overlay{position:fixed;inset:0;z-index:100500;display:flex;flex-direction:column;background:var(--bg,#020617);color:var(--text,#e5e7eb);font:13px/1.4 system-ui,-apple-system,"Segoe UI",sans-serif}
.smap-bar{display:flex;flex-wrap:wrap;align-items:center;gap:8px 12px;padding:10px 16px;border-bottom:1px solid var(--border)}
.smap-title{font-size:15px;font-weight:600;margin-right:4px}
.smap-count{color:var(--muted,#9ca3af);font-size:12px}
.smap-bar input[type=search],.smap-bar select{width:auto;background:#0b1220;color:inherit;border:1px solid var(--border);border-radius:6px;padding:6px 8px;font:inherit;min-height:32px}
.smap-bar input[type=search]{width:220px;max-width:100%}
.smap-chip{border:1px solid var(--border);background:transparent;color:inherit;border-radius:999px;padding:4px 10px;font:inherit;font-size:12px;cursor:pointer;min-height:28px;display:inline-flex;align-items:center;gap:6px}
.smap-chip[aria-pressed=false]{opacity:.45}
.smap-btn{border:1px solid var(--border);background:#0b1220;color:inherit;border-radius:6px;padding:6px 12px;font:inherit;cursor:pointer;min-height:32px}
.smap-btn:hover,.smap-chip:hover{border-color:var(--accent,#38bdf8)}
.smap-btn.primary{background:var(--accent,#38bdf8);color:#03121f;border-color:transparent;font-weight:600}
.smap-spacer{flex:1}
.smap-body{flex:1;display:flex;min-height:0;position:relative}
.smap-canvas{flex:1;min-width:0;position:relative;overflow:hidden;cursor:grab;touch-action:none}
.smap-canvas.dragging{cursor:grabbing}
.smap-canvas svg{width:100%;height:100%;display:block;user-select:none}
.smap-zoom{position:absolute;right:12px;bottom:12px;display:flex;flex-direction:column;gap:6px}
.smap-legend{position:absolute;left:12px;bottom:12px;display:flex;flex-wrap:wrap;gap:6px 12px;background:rgba(2,6,23,.85);border:1px solid var(--border);border-radius:8px;padding:8px 10px;font-size:12px;color:var(--muted)}
.smap-dot{width:9px;height:9px;border-radius:50%;display:inline-block}
.smap-drawer{width:380px;max-width:100%;border-left:1px solid var(--border);overflow:auto;padding:16px 18px 32px;background:#030a1a}
.smap-drawer h2{font-size:17px;margin:0 0 4px}
.smap-drawer h3{font-size:11px;letter-spacing:.06em;text-transform:uppercase;color:var(--muted);margin:18px 0 6px;font-weight:600}
.smap-drawer p{margin:0 0 6px}
.smap-drawer ul{margin:0;padding-left:18px}
.smap-drawer li{margin:2px 0}
.smap-drawer code{font-size:12px;background:#0b1220;border-radius:4px;padding:1px 5px}
.smap-drawer a{color:var(--accent,#38bdf8)}
.smap-tags{display:flex;flex-wrap:wrap;gap:6px}
.smap-tag{border:1px solid var(--border);border-radius:999px;padding:2px 8px;font:inherit;font-size:12px;color:inherit;background:#0b1220;cursor:default;white-space:nowrap}
.smap-tag.link{cursor:pointer}
.smap-tag.link:hover{border-color:var(--accent)}
.smap-empty{color:var(--muted)}
.smap-list{flex:1;overflow:auto;padding:0 16px 24px}
.smap-list table{width:100%;border-collapse:collapse;font-size:13px}
.smap-list th{position:sticky;top:0;background:var(--bg);text-align:left;font-weight:600;padding:10px 8px;border-bottom:1px solid var(--border);cursor:pointer;white-space:nowrap}
.smap-list td{padding:8px;border-bottom:1px solid rgba(148,163,184,.12);vertical-align:top}
.smap-list tr:hover td{background:rgba(56,189,248,.06);cursor:pointer}
.smap-help{position:absolute;top:12px;right:12px;width:420px;max-width:calc(100% - 24px);background:#030a1a;border:1px solid var(--border);border-radius:10px;padding:16px 18px;box-shadow:0 18px 50px rgba(2,6,23,.8);z-index:2}
.smap-help ol{padding-left:20px;margin:8px 0}
.smap-help li{margin:6px 0}
.smap-help code{background:#0b1220;border-radius:4px;padding:1px 5px}
.smap-tile{cursor:pointer}
.smap-tile:focus{outline:none}
.smap-tile:focus rect.bg,.smap-tile.sel rect.bg{stroke:var(--accent,#38bdf8);stroke-width:2}
.smap-panel .smap-row{display:flex;justify-content:space-between;padding:4px 0;border-bottom:1px solid rgba(148,163,184,.12)}
@media (max-width:760px){.smap-drawer{position:absolute;left:0;right:0;bottom:0;width:auto;max-height:60%;border-left:0;border-top:1px solid var(--border)}.smap-bar input[type=search]{width:100%}}
`;

const STATUS_COLOR = { live: "#34d399", beta: "#fbbf24", dev: "#60a5fa", planned: "#94a3b8", retired: "#64748b" };

function ensureStyle() {
  if (document.getElementById("smap-style")) return;
  const s = document.createElement("style");
  s.id = "smap-style";
  s.textContent = STYLE;
  document.head.appendChild(s);
}

function readView() {
  try { return { ...JSON.parse(localStorage.getItem(VIEW_KEY) || "{}") }; } catch { return {}; }
}
function saveView(v) {
  try { localStorage.setItem(VIEW_KEY, JSON.stringify(v)); } catch {}
}

function createRuntime() {
  const model = loadModel();
  const saved = readView();
  const state = {
    q: "",
    statuses: new Set(arr(saved.statuses).length ? saved.statuses : STATUSES),
    area: saved.area || "",
    owner: saved.owner || "",
    view: saved.view === "list" ? "list" : "map",
    selected: null,
    hover: null,
    sort: { key: "name", dir: 1 },
    help: false,
  };
  let overlay = null, svg = null, vb = null, lay = null, keyHandler = null;

  function persist() {
    saveView({ statuses: [...state.statuses], area: state.area, owner: state.owner, view: state.view });
  }

  function matches(m) {
    if (!state.statuses.has(m.status || "planned")) return false;
    if (state.owner === "_none" ? m.owner : state.owner && m.owner !== state.owner) return false;
    if (state.area && m.area !== state.area) return false;
    if (!state.q) return true;
    const hay = [m.id, m.name, m.summary, m.notes, ...arr(m.tools).map((t) => t.name), ...arr(m.paths), ...arr(m.api), ...arr(m.produces), ...arr(m.reads), ...arr(m.external)].join(" ").toLowerCase();
    return state.q.toLowerCase().split(/\s+/).every((w) => hay.includes(w));
  }

  function counts() {
    const tools = model.modules.reduce((n, m) => n + arr(m.tools).length, 0);
    return `${model.modules.length} modules · ${tools} tools · ${model.core.length} core parts · ${model.services.length} services`;
  }

  // ---------- panel (docked) ----------
  function mountPanel(el) {
    ensureStyle();
    const byStatus = STATUSES.map((s) => [s, model.modules.filter((m) => (m.status || "planned") === s).length]).filter(([, n]) => n);
    const unowned = model.modules.filter((m) => !m.owner).length;
    el.innerHTML = `
      <div class="smap-panel" style="padding:12px;display:flex;flex-direction:column;gap:12px">
        <p style="margin:0;color:var(--muted)">Every HydroTools module drawn around the core platform, built from the <code>module.json</code> file next to each tool.</p>
        <button type="button" class="smap-btn primary" data-open>Open system map</button>
        <div>${byStatus.map(([s, n]) => `<div class="smap-row"><span><span class="smap-dot" style="background:${STATUS_COLOR[s]}"></span> ${STATUS_LABEL[s]}</span><b>${n}</b></div>`).join("")}
          <div class="smap-row"><span>No owner yet</span><b>${unowned}</b></div></div>
        <div class="small" style="color:var(--muted)">${esc(counts())}</div>
      </div>`;
    el.querySelector("[data-open]").addEventListener("click", open);
  }

  // ---------- overlay ----------
  function open() {
    ensureStyle();
    if (overlay) return;
    overlay = document.createElement("div");
    overlay.className = "smap-overlay";
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-label", "HydroTools system map");
    document.body.appendChild(overlay);
    keyHandler = (e) => {
      if (e.key !== "Escape") return;
      if (state.help) { state.help = false; render(); } else if (state.selected) { state.selected = null; render(); } else close();
    };
    document.addEventListener("keydown", keyHandler);
    render(true);
  }

  function close() {
    document.removeEventListener("keydown", keyHandler);
    overlay?.remove();
    overlay = null; svg = null;
  }

  function owners() {
    return [...new Set(model.modules.map((m) => m.owner).filter(Boolean))].sort();
  }

  function render(fit) {
    if (!overlay) return;
    const visible = new Set(model.modules.filter(matches).map((m) => m.id));
    overlay.innerHTML = `
      <div class="smap-bar">
        <span class="smap-title">System map</span><span class="smap-count">${esc(counts())}</span>
        <input type="search" placeholder="Search modules, files, APIs" value="${esc(state.q)}" data-q aria-label="Search modules">
        ${STATUSES.map((s) => `<button type="button" class="smap-chip" data-status="${s}" aria-pressed="${state.statuses.has(s)}"><span class="smap-dot" style="background:${STATUS_COLOR[s]}"></span>${STATUS_LABEL[s]}</button>`).join("")}
        <select data-area aria-label="Area"><option value="">All areas</option>${model.areas.map((a) => `<option value="${esc(a.id)}" ${state.area === a.id ? "selected" : ""}>${esc(a.name)}</option>`).join("")}</select>
        <select data-owner aria-label="Owner"><option value="">Any owner</option><option value="_none" ${state.owner === "_none" ? "selected" : ""}>No owner yet</option>${owners().map((o) => `<option ${state.owner === o ? "selected" : ""}>${esc(o)}</option>`).join("")}</select>
        <span class="smap-spacer"></span>
        <button type="button" class="smap-btn" data-view>${state.view === "map" ? "List view" : "Map view"}</button>
        <button type="button" class="smap-btn" data-help>Add a tool</button>
        <button type="button" class="smap-btn" data-close aria-label="Close system map">Close</button>
      </div>
      <div class="smap-body">
        ${state.view === "map" ? `<div class="smap-canvas" data-canvas></div>` : `<div class="smap-list" data-list></div>`}
        ${state.selected ? `<aside class="smap-drawer" data-drawer></aside>` : ""}
        ${state.help ? helpHtml() : ""}
      </div>`;
    wireBar();
    if (state.view === "map") renderMap(visible, fit || !vb);
    else renderList(visible);
    if (state.selected) renderDrawer();
  }

  function wireBar() {
    const q = overlay.querySelector("[data-q]");
    q.addEventListener("input", () => {
      state.q = q.value.trim();
      const pos = q.selectionStart;
      render();
      const nq = overlay.querySelector("[data-q]");
      nq.focus(); nq.setSelectionRange(pos, pos);
    });
    overlay.querySelectorAll("[data-status]").forEach((b) => b.addEventListener("click", () => {
      const s = b.dataset.status;
      if (state.statuses.has(s) && state.statuses.size > 1) state.statuses.delete(s); else state.statuses.add(s);
      persist(); render();
    }));
    overlay.querySelector("[data-area]").addEventListener("change", (e) => { state.area = e.target.value; persist(); render(true); });
    overlay.querySelector("[data-owner]").addEventListener("change", (e) => { state.owner = e.target.value; persist(); render(); });
    overlay.querySelector("[data-view]").addEventListener("click", () => { state.view = state.view === "map" ? "list" : "map"; persist(); render(true); });
    overlay.querySelector("[data-help]").addEventListener("click", () => { state.help = !state.help; render(); });
    overlay.querySelector("[data-close]").addEventListener("click", close);
    overlay.querySelector("[data-help-close]")?.addEventListener("click", () => { state.help = false; render(); });
  }

  function helpHtml() {
    return `<div class="smap-help" role="dialog" aria-label="Add a tool">
      <div style="display:flex;justify-content:space-between;align-items:center"><b>Add a tool</b><button type="button" class="smap-btn" data-help-close>Close</button></div>
      <ol>
        <li>On the dev box, run <code>dev new-tool &lt;name&gt;</code>. It makes <code>src/tools/&lt;name&gt;/</code> with a starter panel, its <code>module.json</code>, and registers it (admin-only to start).</li>
        <li>Fill in <code>module.json</code>: area, status, summary, what it uses and produces. The map draws it from that file.</li>
        <li>Build it, then <code>dev ship</code>. It appears on dev, and on this map, within a couple of minutes.</li>
        <li>CI fails any tool folder without a valid <code>module.json</code> (<code>npm run check:map</code>), so the map can't drift.</li>
      </ol>
      <p class="smap-empty" style="margin:0">Full guide: <code>docs/system-map/README.md</code></p>
    </div>`;
  }

  // ---------- map ----------
  function renderMap(visible, fit) {
    const host = overlay.querySelector("[data-canvas]");
    lay = layout(model, visible);
    const selId = state.selected;
    const NS = "http://www.w3.org/2000/svg";
    const parts = [];
    parts.push(`<defs><marker id="smap-arrow" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="6" markerHeight="6" orient="auto-start-reverse"><path d="M0,0 L8,4 L0,8 z" fill="#38bdf8"/></marker></defs>`);
    parts.push(`<g data-edges></g>`);
    // core card
    const cb = lay.coreBox;
    parts.push(`<rect x="${cb.x}" y="${cb.y}" width="${cb.w}" height="${cb.h}" rx="14" fill="#06142b" stroke="#38bdf8" stroke-opacity=".55"/>`);
    parts.push(`<text x="${cb.x + CL_PAD}" y="${cb.y + 28}" fill="#e5e7eb" font-size="16" font-weight="600">Core platform</text>`);
    parts.push(`<text x="${cb.x + cb.w - CL_PAD}" y="${cb.y + 28}" fill="#9ca3af" font-size="12" text-anchor="end">every tool builds on these</text>`);
    for (const [id, t] of lay.coreTiles) {
      parts.push(`<g class="smap-tile" data-core="${esc(id)}" tabindex="0" role="button" aria-label="${esc(t.c.name)}"><rect class="bg" x="${t.x}" y="${t.y}" width="${t.w}" height="${t.h}" rx="8" fill="#0b2345" stroke="#1e3a5f"/><text x="${t.x + 10}" y="${t.y + 25}" fill="#bae6fd" font-size="13">${esc(clip(t.c.name, 22))}</text><title>${esc(t.c.summary || t.c.name)}</title></g>`);
    }
    if (lay.platTop != null) parts.push(`<text x="${cb.x + CL_PAD}" y="${lay.platTop - 10}" fill="#9ca3af" font-size="12">Platform tools</text>`);
    // clusters
    for (const c of lay.clusters) {
      const prods = productsOf(c.mods);
      parts.push(`<rect x="${c.x}" y="${c.y}" width="${c.w}" height="${c.h}" rx="12" fill="#081226" stroke="rgba(148,163,184,.28)"/>`);
      parts.push(`<text x="${c.x + CL_PAD}" y="${c.y + 28}" fill="#e5e7eb" font-size="15" font-weight="600">${esc(c.area.name)}</text>`);
      parts.push(`<text x="${c.x + c.w - CL_PAD}" y="${c.y + 28}" fill="#9ca3af" font-size="12" text-anchor="end">${c.mods.length}</text>`);
      if (!c.mods.length) parts.push(`<text x="${c.x + CL_PAD}" y="${c.y + CL_HEAD + 24}" fill="#64748b" font-size="12">Nothing matches the filters</text>`);
      const prodText = prods.length ? `Outputs: ${prods.slice(0, 5).join(", ")}${prods.length > 5 ? ` +${prods.length - 5}` : ""}` : "";
      if (prodText) parts.push(`<text x="${c.x + CL_PAD}" y="${c.y + c.h - 14}" fill="#9ca3af" font-size="12">${esc(clip(prodText, Math.floor((c.w - CL_PAD * 2) / 6.6)))}<title>${esc(prods.join(", "))}</title></text>`);
    }
    // module tiles
    for (const [id, t] of lay.tiles) {
      const m = t.m;
      const st = m.status || "planned";
      const dashed = st === "planned" ? ` stroke-dasharray="4 3"` : "";
      const open = openItems(model.roadmapByModule.get(id)).length;
      const ws = m.kind !== "module" ? `<text x="${t.x + t.w - 8}" y="${t.y + 15}" fill="#7dd3fc" font-size="10" text-anchor="end">3D</text>` : "";
      const badge = open ? `<g><rect x="${t.x + t.w - 30}" y="${t.y + t.h - 16}" width="24" height="13" rx="6.5" fill="#1e3a5f"/><text x="${t.x + t.w - 18}" y="${t.y + t.h - 6}" fill="#bae6fd" font-size="10" text-anchor="middle">${open}</text></g>` : "";
      parts.push(`<g class="smap-tile${selId === id ? " sel" : ""}" data-mod="${esc(id)}" tabindex="0" role="button" aria-label="${esc(m.name)}, ${STATUS_LABEL[st]}"${st === "retired" ? ` opacity=".55"` : ""}>
        <rect class="bg" x="${t.x}" y="${t.y}" width="${t.w}" height="${t.h}" rx="8" fill="#0d1a30" stroke="#22324d"${dashed}/>
        <circle cx="${t.x + 13}" cy="${t.y + t.h / 2}" r="4.5" fill="${STATUS_COLOR[st]}"/>
        <text x="${t.x + 24}" y="${t.y + 25}" fill="#e5e7eb" font-size="13"${st === "retired" ? ` text-decoration="line-through"` : ""}>${esc(clip(m.name, 19))}</text>${ws}${badge}
        <title>${esc(m.name)} (${STATUS_LABEL[st]})${m.summary ? ` - ${esc(m.summary)}` : ""}</title></g>`);
    }
    host.innerHTML = `<svg xmlns="${NS}" role="img" aria-label="System map: ${visible.size} modules around the core platform">${parts.join("")}</svg>
      <div class="smap-zoom"><button type="button" class="smap-btn" data-zin aria-label="Zoom in">+</button><button type="button" class="smap-btn" data-zout aria-label="Zoom out">−</button><button type="button" class="smap-btn" data-fit>Fit</button></div>
      <div class="smap-legend">${STATUSES.map((s) => `<span><span class="smap-dot" style="background:${STATUS_COLOR[s]}"></span> ${STATUS_LABEL[s]}</span>`).join("")}<span>3D = part of the 3D Space</span><span>badge = open roadmap items</span></div>`;
    svg = host.querySelector("svg");
    if (fit || !vb) vb = { x: -40, y: -40, w: lay.width + 80, h: lay.height + 80 };
    applyVb();
    wirePanZoom(host);
    svg.querySelectorAll("[data-mod],[data-core]").forEach((g) => {
      const id = g.dataset.mod || g.dataset.core;
      const pick = () => { state.selected = g.dataset.mod ? id : `core:${id}`; render(); };
      g.addEventListener("click", (e) => { if (!g._dragged) pick(); });
      g.addEventListener("keydown", (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); pick(); } });
      g.addEventListener("mouseenter", () => drawEdges(g.dataset.mod ? id : `core:${id}`));
      g.addEventListener("mouseleave", () => drawEdges(state.selected));
    });
    drawEdges(state.selected);
  }

  function clip(s, n) {
    s = String(s || "");
    return s.length > n ? `${s.slice(0, n - 1)}…` : s;
  }

  function drawEdges(focus) {
    const g = svg?.querySelector("[data-edges]");
    if (!g || !lay) return;
    if (!focus) { g.innerHTML = ""; dimFor(null); return; }
    const lines = [];
    const related = new Set();
    if (String(focus).startsWith("core:")) {
      const cid = focus.slice(5);
      const from = lay.coreTiles.get(cid);
      for (const [id, t] of lay.tiles) if (arr(t.m.uses).includes(cid)) {
        lines.push(`<path d="${curve(t, from)}" fill="none" stroke="#38bdf8" stroke-opacity=".5" stroke-width="1.4"/>`);
        related.add(id);
      }
      related.add(focus);
    } else {
      const t = lay.tiles.get(focus);
      if (!t) { g.innerHTML = ""; return; }
      for (const cid of arr(t.m.uses)) {
        const ct = lay.coreTiles.get(cid);
        if (ct) { lines.push(`<path d="${curve(t, ct)}" fill="none" stroke="#38bdf8" stroke-opacity=".45" stroke-width="1.3"/>`); related.add(`core:${cid}`); }
      }
      for (const d of arr(t.m.dependsOn)) {
        const dt = lay.tiles.get(d);
        if (dt) { lines.push(`<path d="${curve(t, dt)}" fill="none" stroke="#fbbf24" stroke-width="1.8" marker-end="url(#smap-arrow)"/>`); related.add(d); }
      }
      for (const d of model.dependents.get(focus) || []) {
        const dt = lay.tiles.get(d);
        if (dt) { lines.push(`<path d="${curve(dt, t)}" fill="none" stroke="#a3e635" stroke-width="1.5" stroke-dasharray="5 4"/>`); related.add(d); }
      }
      related.add(focus);
    }
    g.innerHTML = lines.join("");
    dimFor(related);
  }

  function dimFor(related) {
    svg?.querySelectorAll("[data-mod]").forEach((el) => {
      el.style.opacity = !related || related.has(el.dataset.mod) ? "" : ".35";
    });
    svg?.querySelectorAll("[data-core]").forEach((el) => {
      el.style.opacity = !related || related.has(`core:${el.dataset.core}`) ? "" : ".45";
    });
  }

  function applyVb() {
    svg?.setAttribute("viewBox", `${vb.x} ${vb.y} ${vb.w} ${vb.h}`);
  }

  function zoomAt(factor, cx, cy) {
    const nw = Math.min(Math.max(vb.w * factor, 300), (lay.width + 80) * 3);
    const k = nw / vb.w;
    vb = { x: cx - (cx - vb.x) * k, y: cy - (cy - vb.y) * k, w: nw, h: vb.h * k };
    applyVb();
  }

  function toSvg(host, clientX, clientY) {
    const r = host.getBoundingClientRect();
    const scale = Math.max(vb.w / r.width, vb.h / r.height);
    const ox = vb.x + (vb.w - r.width * scale) / 2, oy = vb.y + (vb.h - r.height * scale) / 2;
    return { x: ox + (clientX - r.left) * scale, y: oy + (clientY - r.top) * scale, scale };
  }

  function wirePanZoom(host) {
    host.addEventListener("wheel", (e) => {
      e.preventDefault();
      const p = toSvg(host, e.clientX, e.clientY);
      zoomAt(e.deltaY > 0 ? 1.12 : 1 / 1.12, p.x, p.y);
    }, { passive: false });
    let drag = null;
    host.addEventListener("pointerdown", (e) => {
      if (e.button !== 0 || e.target.closest(".smap-zoom,.smap-legend")) return;
      drag = { x: e.clientX, y: e.clientY, vb: { ...vb }, moved: false, id: e.pointerId, target: e.target.closest("[data-mod],[data-core]") };
    });
    host.addEventListener("pointermove", (e) => {
      if (!drag) return;
      const dx = e.clientX - drag.x, dy = e.clientY - drag.y;
      if (!drag.moved && Math.hypot(dx, dy) < 4) return;
      if (!drag.moved) host.setPointerCapture(drag.id); // only once it's a drag, so plain clicks still reach the tile
      drag.moved = true;
      host.classList.add("dragging");
      const { scale } = toSvg(host, e.clientX, e.clientY);
      vb = { ...drag.vb, x: drag.vb.x - dx * scale, y: drag.vb.y - dy * scale };
      applyVb();
    });
    const end = () => {
      if (drag?.target) drag.target._dragged = drag.moved;
      setTimeout(() => { if (drag?.target) drag.target._dragged = false; }, 0);
      drag = null;
      host.classList.remove("dragging");
    };
    host.addEventListener("pointerup", end);
    host.addEventListener("pointercancel", end);
    const mid = () => ({ x: vb.x + vb.w / 2, y: vb.y + vb.h / 2 });
    host.querySelector("[data-zin]").addEventListener("click", () => { const m = mid(); zoomAt(1 / 1.3, m.x, m.y); });
    host.querySelector("[data-zout]").addEventListener("click", () => { const m = mid(); zoomAt(1.3, m.x, m.y); });
    host.querySelector("[data-fit]").addEventListener("click", () => { vb = { x: -40, y: -40, w: lay.width + 80, h: lay.height + 80 }; applyVb(); });
  }

  // ---------- list ----------
  function renderList(visible) {
    const host = overlay.querySelector("[data-list]");
    const areaName = (id) => model.areas.find((a) => a.id === id)?.name || id || "";
    const rows = model.modules.filter((m) => visible.has(m.id)).map((m) => ({
      m, name: m.name, area: areaName(m.area), status: STATUS_LABEL[m.status || "planned"], owner: m.owner || "",
      tools: arr(m.tools).length, roadmap: openItems(model.roadmapByModule.get(m.id)).length,
    }));
    const { key, dir } = state.sort;
    rows.sort((a, b) => (typeof a[key] === "number" ? a[key] - b[key] : String(a[key]).localeCompare(String(b[key]))) * dir);
    const cols = [["name", "Module"], ["area", "Area"], ["status", "Status"], ["owner", "Owner"], ["tools", "Tools"], ["roadmap", "Open roadmap"]];
    host.innerHTML = `<table><thead><tr>${cols.map(([k, l]) => `<th data-sort="${k}" aria-sort="${key === k ? (dir > 0 ? "ascending" : "descending") : "none"}">${l}${key === k ? (dir > 0 ? " ▲" : " ▼") : ""}</th>`).join("")}<th>Summary</th></tr></thead>
      <tbody>${rows.map((r) => `<tr data-mod="${esc(r.m.id)}" tabindex="0"><td><span class="smap-dot" style="background:${STATUS_COLOR[r.m.status || "planned"]}"></span> ${esc(r.name)}${r.m.kind !== "module" ? ` <span class="smap-tag">3D Space</span>` : ""}</td><td>${esc(r.area)}</td><td>${esc(r.status)}</td><td>${r.owner ? esc(r.owner) : `<span class="smap-empty">None</span>`}</td><td>${r.tools || ""}</td><td>${r.roadmap || ""}</td><td>${esc(r.m.summary || "")}</td></tr>`).join("")}</tbody></table>
      ${rows.length ? "" : `<p class="smap-empty" style="padding:16px 0">Nothing matches the filters.</p>`}`;
    host.querySelectorAll("[data-sort]").forEach((th) => th.addEventListener("click", () => {
      const k = th.dataset.sort;
      state.sort = { key: k, dir: state.sort.key === k ? -state.sort.dir : 1 };
      render();
    }));
    host.querySelectorAll("tr[data-mod]").forEach((tr) => {
      const pick = () => { state.selected = tr.dataset.mod; render(); };
      tr.addEventListener("click", pick);
      tr.addEventListener("keydown", (e) => { if (e.key === "Enter") pick(); });
    });
  }

  // ---------- drawer ----------
  function tagList(items, linkKind) {
    const list = arr(items).filter(Boolean);
    if (!list.length) return `<span class="smap-empty">None listed</span>`;
    return `<div class="smap-tags">${list.map((v) => {
      const s = typeof v === "string" ? v : v.name || v.id;
      const target = linkKind === "core" ? `core:${v}` : linkKind === "mod" && model.byId.has(v) ? v : "";
      const label = linkKind === "core" ? model.coreById.get(v)?.name || v : linkKind === "mod" ? model.byId.get(v)?.name || v : s;
      return target ? `<button type="button" class="smap-tag link" data-goto="${esc(target)}">${esc(label)}</button>` : `<span class="smap-tag">${esc(label)}</span>`;
    }).join("")}</div>`;
  }

  function pathList(paths) {
    const list = arr(paths).filter(Boolean);
    if (!list.length) return `<span class="smap-empty">None listed</span>`;
    return `<ul>${list.map((p) => `<li><a href="${REPO_TREE}${esc(String(p).replace(/^\/+/, ""))}" target="_blank" rel="noopener"><code>${esc(p)}</code></a></li>`).join("")}</ul>`;
  }

  function renderDrawer() {
    const host = overlay.querySelector("[data-drawer]");
    const sel = state.selected;
    let html = "";
    if (sel.startsWith("core:")) {
      const c = model.coreById.get(sel.slice(5));
      const users = model.modules.filter((m) => arr(m.uses).includes(c?.id));
      html = `<h2>${esc(c?.name || sel)}</h2><p class="smap-empty">Core platform</p><p>${esc(c?.summary || "")}</p>
        <h3>Code</h3>${pathList(c?.paths)}
        <h3>Used by ${users.length} module${users.length === 1 ? "" : "s"}</h3>${tagList(users.map((m) => m.id), "mod")}`;
    } else {
      const m = model.byId.get(sel);
      if (!m) { state.selected = null; return; }
      const st = m.status || "planned";
      const rm = arr(model.roadmapByModule.get(m.id));
      const area = model.areas.find((a) => a.id === m.area);
      html = `<h2>${esc(m.name)}</h2>
        <p><span class="smap-dot" style="background:${STATUS_COLOR[st]}"></span> ${STATUS_LABEL[st]} · ${esc(area?.name || m.area || "No area")}${m.workspaceName ? ` · part of ${esc(m.workspaceName)}` : ""}</p>
        <p>${esc(m.summary || "")}</p>
        <p><b>Owner:</b> ${m.owner ? esc(m.owner) : `<span class="smap-empty">Nobody yet</span>`}</p>
        ${arr(m.tools).length ? `<h3>Tools in the app</h3>${tagList(arr(m.tools).map((t) => t.name))}` : ""}
        <h3>Builds on (core)</h3>${tagList(m.uses, "core")}
        <h3>Depends on</h3>${tagList(m.dependsOn, "mod")}
        <h3>Used by</h3>${tagList(model.dependents.get(m.id), "mod")}
        <h3>Services and APIs</h3>${tagList([...arr(m.services), ...arr(m.api)])}
        <h3>Reads</h3>${tagList(m.reads)}
        <h3>Produces</h3>${tagList(m.produces)}
        ${arr(m.external).length ? `<h3>Outside data</h3>${tagList(m.external)}` : ""}
        <h3>Roadmap</h3>${rm.length ? `<ul>${rm.map((i) => `<li>${i.url ? `<a href="${esc(i.url)}" target="_blank" rel="noopener">${esc(i.title)}</a>` : esc(i.title)}${i.status ? ` <span class="smap-empty">(${esc(i.status)})</span>` : ""}</li>`).join("")}</ul>` : `<span class="smap-empty">No linked items</span>`}
        <h3>Code</h3>${pathList(m.paths)}
        ${arr(m.access).length ? `<h3>Who can use it</h3>${tagList(["admin", ...arr(m.access)])}` : `<h3>Who can use it</h3><span class="smap-empty">Admins only</span>`}
        ${m.notes ? `<h3>Notes</h3><p>${esc(m.notes)}</p>` : ""}
        <h3>Edit this entry</h3><p><code>${esc(m.kind === "module" ? `src/tools/${m.id}/module.json` : "docs/system-map/workspaces.json")}</code></p>`;
    }
    host.innerHTML = `<div style="display:flex;justify-content:flex-end"><button type="button" class="smap-btn" data-drawer-close aria-label="Close details">Close</button></div>${html}`;
    host.querySelector("[data-drawer-close]").addEventListener("click", () => { state.selected = null; render(); });
    host.querySelectorAll("[data-goto]").forEach((b) => b.addEventListener("click", () => { state.selected = b.dataset.goto; render(); }));
  }

  return { mountPanel, open, close };
}

let runtime = null;

export const systemMapTool = {
  id: "system-map",
  name: "System Map",
  init() { runtime = runtime ?? createRuntime(); },
  destroy() { runtime?.close(); },
  renderPanel(app, containerEl) {
    runtime = runtime ?? createRuntime();
    runtime.mountPanel(containerEl);
  },
};
